// TelcoTrack — D5 occupancy schema (schéma osazení) mapped from weld-layout v2.
// Numbering layers stay separate: cabinet position ≠ adapter ≠ connector ≠ fiber ≠ splice.

import type { RozvadecCabinetTypeId, RozvadecSplitterRatio } from './rozvadec-cabinet.constants';
import type {
  RozvadecCassetteAdapterFamily,
  RozvadecCassetteConnectorPolarity,
  RozvadecCassetteTypeId,
} from './rozvadec-cassette.constants';
import type { RsuOccupancy } from './rozvadec-rsu-occupancy.types';
import type { RozvadecWeldIncomingRef, RozvadecWeldTargetRef } from './rozvadec-weld-layout.types';

export const ROZVADEC_OCCUPANCY_SCHEMA_VERSION = 1 as const;
export type RozvadecOccupancySchemaVersion = typeof ROZVADEC_OCCUPANCY_SCHEMA_VERSION;

export const ROZVADEC_OCCUPANCY_RENDERER_IDS = [
  'D5-MUK',
  'D5-PON32',
  'D5-RAYTR3',
  'D5-ORSM',
  'D5-SPOJKA',
  'D5-HUAWEI',
] as const;
export type RozvadecOccupancyRendererId = (typeof ROZVADEC_OCCUPANCY_RENDERER_IDS)[number];

export const ROZVADEC_OCCUPANCY_BLOCKER_CODES = [
  'MUK24_ROW_INVALID',
  'MUK48_ROWS_INVALID',
  'SAP_TYPE_CONFLICT',
  'KM5_START_ON_24',
  'KM5_OVERLAP',
  'KM5_SC_PORT_EXCEEDED',
  'KM5_LC_PORT_EXCEEDED',
  'KM5_LC_LABEL_INVALID',
  'SPLITTER_1X16_SHAPE_INVALID',
  'SPLITTER_2X8_SHAPE_INVALID',
  'SPLITTER_1X4_SHAPE_INVALID',
  'SPLITTER_2X4_SHAPE_INVALID',
  'SPLITTER_OUTPUT_MULTI_DROP',
  'PON32_SPLITTER_INSIDE_KM6',
  'PON32_OUTPUT_MISSING_KM6_SPLICE',
  'PON32_WELD_EXCEEDS_KM6',
  'CONNECTOR_DOUBLE_OCCUPIED',
  'SPLICE_DOUBLE_OCCUPIED',
  'FIBER_NUMBER_MISSING',
  'ADDRESS_ON_SPLITTER_INPUT',
  'RESERVE_MARKED_IN_SERVICE',
  'SDF_1X4_NOT_USED',
  'RENDERER_UNSUPPORTED',
] as const;
export type RozvadecOccupancyBlockerCode = (typeof ROZVADEC_OCCUPANCY_BLOCKER_CODES)[number];

export type RozvadecOccupancyCabinetCode = 'MUK24' | 'MUK48' | 'PON32' | 'RAYTR3' | 'OTHER';

/** Network-point function. Never the same as the physical cabinet (MUK / RAY-TR3). */
export const ROZVADEC_NETWORK_ROLES = ['SDF', 'aSDF', 'none'] as const;
export type RozvadecNetworkRole = (typeof ROZVADEC_NETWORK_ROLES)[number];

export const ROZVADEC_OCCUPANCY_SEAT_STATUSES = [
  'obsazeno',
  'rez.',
  'volno',
  'plán',
  'volná-pozice',
  'obsazená-pozice',
  'neexistuje',
] as const;
export type RozvadecOccupancySeatStatus = (typeof ROZVADEC_OCCUPANCY_SEAT_STATUSES)[number];

export const ROZVADEC_OCCUPANCY_SHEET_KINDS = ['muk-row', 'ray-stand'] as const;
export type RozvadecOccupancySheetKind = (typeof ROZVADEC_OCCUPANCY_SHEET_KINDS)[number];

export const ROZVADEC_OCCUPANCY_PAGE_FORMATS = ['A3-landscape', 'A3-portrait'] as const;
export type RozvadecOccupancyPageFormat = (typeof ROZVADEC_OCCUPANCY_PAGE_FORMATS)[number];

export const ROZVADEC_MUK_CONFIGURATIONS = ['1×24', '2×24'] as const;
export type RozvadecMukConfiguration = (typeof ROZVADEC_MUK_CONFIGURATIONS)[number];

export type RozvadecOccupancyPortCell = {
  id: string;
  printLabel: string;
  status: RozvadecOccupancySeatStatus;
  source: string;
  target: string;
  hopRef: string | null;
  colorLabel: string | null;
  logicalFiber: string | null;
};

export type RozvadecOccupancyCassetteBlock = {
  cassetteId: string | null;
  cassetteType: string | null;
  printLabel: string;
  startPosition: number;
  span: number;
  kind:
    | 'KM5_SC'
    | 'KM5_LC'
    | 'KM4'
    | 'KM5_SPLITTER'
    | 'KM6'
    | 'OFSM'
    | 'SPLITTER_MODULE'
    | 'JUMPER'
    | 'OCCUPIED_OPAQUE'
    | 'empty';
  ports: RozvadecOccupancyPortCell[];
  routeLabel: string;
  /** OFSM column count (12 for 144, 8 for 96, 4 for 48). */
  columnCount?: number;
  /** Physical rack height in U (RAY-TR3 stand). */
  heightU?: number;
};

export type RozvadecOccupancyPositionSlot = {
  position: number;
  /** Left scale: KM5 span-2 shows both numbers on one merged block. */
  scaleLabels: number[];
  empty: boolean;
  isCassetteStart: boolean;
  /** False for the lower half of a merged KM5 — do not emit a second row. */
  render: boolean;
  cassette: RozvadecOccupancyCassetteBlock | null;
};

export type RozvadecOccupancyDoorSplitter = {
  id: string;
  printLabel: string;
  ratio: string;
  input: RozvadecOccupancyPortCell;
  outputs: RozvadecOccupancyPortCell[];
};

export type RozvadecOccupancySheetMerge = {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
};

export type RozvadecOccupancySheetLayout = {
  merges: RozvadecOccupancySheetMerge[];
  columnWidths: number[];
  rowHeights: number[];
  pageOrientation: 'portrait' | 'landscape';
  pageSize: 'A3';
  fills: Array<{ row: number; col: number; argb: string }>;
};

export type RozvadecOccupancySheet = {
  id: string;
  kind: RozvadecOccupancySheetKind;
  title: string;
  rowId: string;
  rowLabel: string;
  sideLabel: string | null;
  frontView: true;
  pageFormat: RozvadecOccupancyPageFormat;
  sapCode: string;
  configuration: string;
  networkRole: RozvadecNetworkRole;
  locality: string;
  cabinetTypeLabel: string;
  printName: string;
  objectName: string;
  statusLabel: string;
  positions: RozvadecOccupancyPositionSlot[];
  doorSplitters: RozvadecOccupancyDoorSplitter[];
  references: string[];
  notes: string[];
  sheetIndex: number;
  sheetCount: number;
};

export type RozvadecFiberEndpoint = {
  id: string;
  cableId: string;
  physicalFiber: number | null;
  logicalFiber: number | null;
  firstFeederCableId: string | null;
  role: 'feeder' | 'drop' | 'reserve' | 'pigtail';
};

export type RozvadecOccupancyCassette = {
  id: string;
  cassetteType: RozvadecCassetteTypeId;
  printLabel: string;
  rowId: string;
  rowLabel: string;
  /** 1-based start / upper position. */
  startPosition: number;
  displayPosition: number;
  occupiedPositions: number[];
  adapterFamily: RozvadecCassetteAdapterFamily | null;
};

export type RozvadecOccupancyConnector = {
  id: string;
  cassetteId: string;
  adapterPosition: number;
  polarity: RozvadecCassetteConnectorPolarity | null;
  printLabel: string;
  incoming: RozvadecWeldIncomingRef | null;
  pigtailSpliceId: string | null;
};

export type RozvadecOccupancySplice = {
  id: string;
  cassetteId: string;
  seat: number;
  printLabel: string;
  kind: 'tray' | 'pigtail';
  fibers: RozvadecWeldIncomingRef[];
};

export type RozvadecOccupancySplitterPort = {
  id: string;
  splitterId: string;
  direction: 'in' | 'out';
  splitterIndex: number;
  port: number;
  printLabel: string;
  /** Cabinet row of the parity half (KM5 preinstalled only). */
  rowPosition: number | null;
  incoming: RozvadecWeldIncomingRef | null;
  target: RozvadecWeldTargetRef | null;
};

export type RozvadecOccupancySplitter = {
  id: string;
  printLabel: string;
  cassetteId: string | null;
  ratio: RozvadecSplitterRatio;
  splitterIndex: number;
  /** False for PON32 — body is outside KM6. */
  insideCassette: boolean;
  inputs: RozvadecOccupancySplitterPort[];
  outputs: RozvadecOccupancySplitterPort[];
};

export type RozvadecOccupancyServiceAssignment = {
  id: string;
  buildingId: string;
  cableId: string;
  physicalFiber: number;
  logicalFiber: number | null;
  /** Feeder / incoming physical fiber (napřímo mid-line color). */
  sourcePhysicalFiber: number | null;
  via: 'connector' | 'splitter' | 'direct';
  connectorId: string | null;
  splitterPortId: string | null;
  spliceId: string | null;
};

export type RozvadecOccupancyGraph = {
  fibers: RozvadecFiberEndpoint[];
  cassettes: RozvadecOccupancyCassette[];
  connectors: RozvadecOccupancyConnector[];
  splices: RozvadecOccupancySplice[];
  splitters: RozvadecOccupancySplitter[];
  assignments: RozvadecOccupancyServiceAssignment[];
};

export type RozvadecOccupancyRow = {
  id: string;
  label: string;
  positions: number;
  fillFrom: 'bottom' | 'top';
};

export type RozvadecPon32Hardware = {
  pon32HardwareRevision: string | null;
  connectorPanelConfiguration: string | null;
  availableKm6Count: number;
};

export type RozvadecOccupancySchema = {
  version: RozvadecOccupancySchemaVersion;
  rendererId: RozvadecOccupancyRendererId;
  cabinetCode: RozvadecOccupancyCabinetCode;
  typeId: RozvadecCabinetTypeId | null;
  sapCode: string | null;
  printName: string;
  /** SDF / aSDF is a network function, not the physical case. */
  networkRole: RozvadecNetworkRole;
  locality: string | null;
  objectName: string | null;
  configuration: string | null;
  rows: RozvadecOccupancyRow[];
  graph: RozvadecOccupancyGraph;
  /** Physical monter sheets: MUK cassette row or RAY-TR3 stand. */
  sheets: RozvadecOccupancySheet[];
  pon32: RozvadecPon32Hardware | null;
  rsuOccupancy?: RsuOccupancy | null;
};

export type RozvadecOccupancyBlocker = {
  code: RozvadecOccupancyBlockerCode;
  path: string;
  message: string;
};

export type RozvadecOccupancyGenerateResult = {
  schema: RozvadecOccupancySchema;
  blockers: RozvadecOccupancyBlocker[];
  exportable: boolean;
};

export type RozvadecOccupancyFiberNumber = {
  cableId: string;
  physicalFiber: number;
  logicalFiber: number | null;
  firstFeederCableId: string | null;
};

export type RozvadecOccupancyGenerateInput = {
  typeId?: string | null;
  derivedFromSap?: string | null;
  materials?: ReadonlyArray<{ sapCode?: string | null }> | null;
  incomingCables?: ReadonlyArray<{
    cableId: string;
    typOk?: string | null;
    fiberCount?: number | null;
    sourceRozvadecName?: string | null;
    destRozvadecName?: string | null;
  }> | null;
  buildings?: ReadonlyArray<{
    buildingId: string;
    cableId?: string | null;
    typOk?: string | null;
    fiberCount?: number | null;
    label?: string | null;
  }> | null;
  buildingLabels?: ReadonlyArray<{ buildingId: string; label: string }> | null;
  networkRole?: RozvadecNetworkRole | null;
  objectName?: string | null;
  locality?: string | null;
  projectLabel?: string | null;
  fiberNumbers?: readonly RozvadecOccupancyFiberNumber[] | null;
  pon32HardwareRevision?: string | null;
  connectorPanelConfiguration?: string | null;
  availableKm6Count?: number | null;
  rsuOccupancy?: RsuOccupancy | null;
  stojanLabel?: string | null;
  cabinetPrintName?: string | null;
};

export type RozvadecOccupancyArtifact = {
  v: 1;
  generatedAt: string;
  generatedFromRevision: number;
  exportable: boolean;
  rendererId: RozvadecOccupancyRendererId;
  blockers: RozvadecOccupancyBlocker[];
  schema: RozvadecOccupancySchema;
};
